import { DEFAULT_CURRENCY_CODE } from 'client/js/main/components/analytics/LightweightStickyBar/CurrencySwitcher/CurrencySwitcher.constants';
import type { RootState } from 'client/js/redux';
import { createSelector } from 'reselect';
import { EMAIL_ANALYTICS_NAMESPACE } from '../../../../../../shared/constants';
import { LIGHTWEIGHT_FILTERS_FOR_EMAIL } from '../../../../../../shared/constants/flipperFlags';
import { LIGHTWEIGHT_FILTERS_NAMESPACES_WHITELIST } from '../../../../../../shared/constants/lightweightFilters';
import type { ShareableData, SingleNamespaceData } from '../../../../common/types/shareableTypes';
import { isFeatureEnabled } from '../../../../redux/domains/FlipperUpdates/Flipper.selectors';
import type { Query } from '../../../types/druidTypes';
import { CONTEXTS } from './LightweightFilters.constants';

const selectShareableData: (state: RootState) => ShareableData = createSelector(
  (state: RootState): ShareableData => state.shareable.data,
  (shareableData): ShareableData => shareableData || {}
);

export const selectShareableDataByNamespace: (state: RootState, namespace: string) => SingleNamespaceData = createSelector(
  selectShareableData,
  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  (state: RootState, namespace: string): string => namespace,
  (shareableData, namespace): SingleNamespaceData => shareableData[namespace] || {}
);

export const selectShareableDataByNamespaceAndContext: (state: RootState, namespace: string) => any = createSelector(
  selectShareableDataByNamespace,
  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  (state: RootState, namespace: string): string => CONTEXTS[namespace].table,
  (shareableDataByNamespace, context): any => shareableDataByNamespace[context] || {}
);

export const selectCheckboxesValues: (state: RootState, namespace: string) => [boolean, boolean] = createSelector(
  selectShareableDataByNamespaceAndContext,
  (shareableDataByContext): [boolean, boolean] => [!!shareableDataByContext.organic, !!shareableDataByContext.unique]
);

export const selectSplitSkanCheckboxValue: (state: RootState, namespace: string) => boolean = createSelector(
  selectShareableDataByNamespace,
  (shareableDataByNamespace): boolean => !!shareableDataByNamespace.splitSkanData
);

export const selectQueryValue: (state: RootState, namespace: string) => Query = createSelector(
  selectShareableDataByNamespaceAndContext,
  (shareableDataByContext): Query => shareableDataByContext.query || {}
);

export const selectRunFullReport: (state: RootState, namespace: string) => boolean = createSelector(
  selectShareableDataByNamespaceAndContext,
  (shareableDataByContext): boolean => !!shareableDataByContext.runFullReport
);

const selectIsEmailLightweightFiltersEnabled = createSelector(
  (state: RootState) => isFeatureEnabled(state, LIGHTWEIGHT_FILTERS_FOR_EMAIL),
  (emailEnabled) => emailEnabled
);

export const selectLightweightFiltersEnabled: (state: RootState, namespace: string) => boolean = createSelector(
  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  (state: any, namespace: string): string => namespace,
  selectIsEmailLightweightFiltersEnabled,
  (namespace: string, emailEnabled: boolean): boolean => {
    const lightweightFiltersNamespaceWhitelist = LIGHTWEIGHT_FILTERS_NAMESPACES_WHITELIST;

    if (emailEnabled) {
      lightweightFiltersNamespaceWhitelist.push(EMAIL_ANALYTICS_NAMESPACE);
    }

    return lightweightFiltersNamespaceWhitelist.includes(namespace);
  }
);

export const selectCurrency: (state: RootState, namespace: string) => string = createSelector(
  selectShareableDataByNamespaceAndContext,
  (shareableDataByContext) => shareableDataByContext.currency || DEFAULT_CURRENCY_CODE
);
